import { SetStateAction, useEffect, useMemo, useState } from 'react';

import { ResourceTag, ResourceType } from '@/types/schema';

import { useStoredResourceTags } from './useStoredResourceTags/useStoredResourceTags';
import {
  useCreateTagging,
  useDeleteTagging,
  useListTagsForResource,
} from './useSwr';

export function useTagsOnResource(
  resourceId?: string,
  resourceType?: ResourceType
) {
  const { data: tags, isLoading } = useListTagsForResource(
    resourceType,
    resourceId
  );

  const createTaggingMutation = useCreateTagging();
  const deleteTaggingMutation = useDeleteTagging();

  const asResourceTags = useMemo<
    (ResourceTag & { taggingId?: string })[]
  >(() => {
    return (
      tags?.flatMap((tag) => {
        return tag.values.map((value) => ({
          tag_key_id: tag.id,
          tag_key: tag.key,
          tag_value: value.value,
          tag_value_id: value.id,
          // There should be 1 tagging per tag value
          // but use optional chaining just to be sure
          taggingId: value.taggings?.[0]?.id,
        }));
      }) ?? []
    );
  }, [tags]);

  const setTagSelection = (action: SetStateAction<ResourceTag[]>) => {
    let afterSelection = asResourceTags;
    if (typeof action === 'function') {
      afterSelection = action(asResourceTags);
    } else {
      afterSelection = action;
    }

    if (!resourceType || !resourceId) return;

    // Find all new tags to create
    const tagsToCreate = afterSelection.filter(
      (tag) =>
        !asResourceTags.some(
          (existing) => existing.tag_value_id === tag.tag_value_id
        )
    );

    // Find all existing tags to delete
    const tagsToDelete = asResourceTags.filter(
      (tag) =>
        !afterSelection.some(
          (selected) => selected.tag_value_id === tag.tag_value_id
        )
    );

    // Create all new tags
    tagsToCreate.forEach((newTag) => {
      createTaggingMutation.trigger({
        json: {
          tag_value_id: newTag.tag_value_id,
          resource_type: resourceType,
          resource_id: resourceId,
        },
      });
    });

    // Delete all removed tags
    tagsToDelete.forEach((oldTag) => {
      if (oldTag.taggingId) {
        deleteTaggingMutation.trigger({
          templateUrlParams: {
            taggingId: oldTag.taggingId,
          },
        });
      }
    });
  };

  return {
    tags: asResourceTags,
    setTagSelection,
    isMutating:
      createTaggingMutation.isMutating || deleteTaggingMutation.isMutating,
    isLoading,
    error: createTaggingMutation.error || deleteTaggingMutation.error,
  };
}

export function useCreateTagsOnResource(resourceType: ResourceType) {
  const createTaggingMutation = useCreateTagging();
  const { selectedTags } = useStoredResourceTags();

  const [tagsOnResource, setTagsOnResource] = useState<ResourceTag[]>([]);

  useEffect(() => {
    setTagsOnResource(selectedTags);
  }, [selectedTags]);

  const createTaggings = async (resourceId: string) => {
    const taggingsPromises = tagsOnResource.map((tag) => {
      return createTaggingMutation.trigger({
        json: {
          tag_value_id: tag.tag_value_id,
          resource_type: resourceType,
          resource_id: resourceId,
        },
      });
    });

    await Promise.all(taggingsPromises);
  };

  return {
    trigger: createTaggings,
    isMutating: createTaggingMutation.isMutating,
    tagsOnResource,
    setTagsOnResource,
  };
}
